// ═══════════════════════════════════════════════════
//  Component Nesting Preview
//
//  Packs the panels of a SINGLE component template so the component modal can
//  show how much sheet the parts really occupy. Diagnostic only — the
//  authoritative cut list is the project nesting run, which additionally
//  splits over-long parts across sheets and pools parts from every line item.
//
//  Uses the same guillotine packer as that run, and the same
//  sheet/border/kerf/grain precedence, so the picture is representative
//  rather than a second algorithm. Lives in shared so the planned
//  batch component-nesting endpoint can reuse it server-side unchanged.
// ═══════════════════════════════════════════════════

import type { MaterialLibrary } from '../schemas/material-library.schema';
import {
  type Part,
  type Sheet,
  guillotinePack,
  resolveNestingParams,
  hasGrain,
  sheetUsedArea,
} from './guillotine';

/**
 * One rectangle the component contributes, as emitted by the cost preview
 * utils. `qty` is per single unit of the component.
 */
export interface ComponentNestingPart {
  name: string;
  width_in: number;
  height_in: number;
  qty: number;
  material_id: string;
  /** Waste factor the cost path applies to this piece (0.10 = 10%). */
  waste_factor: number;
  /** False for parts the cost path treats as grain-locked regardless of material. */
  can_rotate?: boolean;
}

export interface ComponentMaterialNesting {
  material_id: string;
  material_name: string;
  unit: string;
  sheet_w: number;
  sheet_h: number;
  border: number;
  kerf: number;
  grain_locked: boolean;
  /** Raw geometry, no waste. */
  parts_sqft: number;
  /** What the cost calculation is charging for (parts_sqft × (1 + waste)). */
  sqft_with_waste: number;
  /** Sheets the packer actually opened. */
  sheet_count: number;
  /** sheet_count × sheet area — the material genuinely consumed if cut alone. */
  consumed_sqft: number;
  /** Placed area ÷ opened sheet area, as a percentage. */
  efficiency_pct: number;
  /**
   * Waste factor that would make the charged area equal the sheets actually
   * opened. Only meaningful at higher unit quantities — a single unit almost
   * always opens one mostly-empty sheet.
   */
  implied_waste_pct: number;
  sheets: Sheet[];
  skipped: Part[];
  part_count: number;
}

/** Materials that are cut from sheet stock, and so can be nested. */
function isSheetGoods(mat: MaterialLibrary): boolean {
  return mat.unit === 'sheet' || mat.unit === 'roll';
}

/**
 * Pack the emitted parts, grouped by material.
 *
 * @param parts      rectangles for ONE unit of the component
 * @param materials  material lookup
 * @param unitQty    how many units of the component to nest together; the
 *                   pooling effect is the whole point of this control
 * @param companyKerf fallback kerf when a material has no nesting_spacing
 */
export function packComponentParts(
  parts: ComponentNestingPart[],
  materials: Map<string, MaterialLibrary>,
  unitQty = 1,
  companyKerf?: number | null,
): ComponentMaterialNesting[] {
  const byMaterial = new Map<string, ComponentNestingPart[]>();
  for (const p of parts) {
    if (!p.material_id || p.width_in <= 0 || p.height_in <= 0 || p.qty <= 0) continue;
    const list = byMaterial.get(p.material_id);
    if (list) list.push(p);
    else byMaterial.set(p.material_id, [p]);
  }

  const out: ComponentMaterialNesting[] = [];

  for (const [materialId, group] of byMaterial) {
    const mat = materials.get(materialId);
    if (!mat) continue;

    const partsSqft = group.reduce(
      (s, p) => s + (p.width_in * p.height_in * p.qty * unitQty) / 144,
      0,
    );
    const sqftWithWaste = group.reduce(
      (s, p) =>
        s +
        ((p.width_in * p.height_in * p.qty * unitQty) / 144) * (1 + p.waste_factor),
      0,
    );

    // Non-sheet goods (sq ft / lin ft) are bought by area or length, never nested.
    if (!isSheetGoods(mat)) {
      out.push({
        material_id: materialId,
        material_name: mat.name,
        unit: mat.unit ?? '',
        sheet_w: 0, sheet_h: 0, border: 0, kerf: 0,
        grain_locked: false,
        parts_sqft: partsSqft,
        sqft_with_waste: sqftWithWaste,
        sheet_count: 0,
        consumed_sqft: 0,
        efficiency_pct: 0,
        implied_waste_pct: 0,
        sheets: [],
        skipped: [],
        part_count: group.reduce((s, p) => s + p.qty * unitQty, 0),
      });
      continue;
    }

    const { sheetW, sheetH, border, kerf } = resolveNestingParams(mat as any, companyKerf);
    const grainLocked = hasGrain(mat as any);

    // Expand qty into individual rectangles, one per physical piece.
    const usableW = sheetW - 2 * border;
    const usableH = sheetH - 2 * border;
    const flat: Part[] = [];

    for (const p of group) {
      const canRotate = grainLocked ? false : (p.can_rotate ?? true);
      const total = p.qty * unitQty;

      // A piece longer than the stock is cut into equal segments, the same way
      // the server's part-collector subdivides an over-long rail.
      let w = p.width_in;
      let h = p.height_in;
      let segments = 1;
      const fits =
        (w <= usableW && h <= usableH) ||
        (canRotate && h <= usableW && w <= usableH);
      if (!fits) {
        if (h > usableH && w <= usableW) {
          segments = Math.ceil(h / usableH);
          h = h / segments;
        } else if (w > usableW && h <= usableH) {
          segments = Math.ceil(w / usableW);
          w = w / segments;
        }
      }

      for (let n = 1; n <= total * segments; n++) {
        flat.push({
          width: w,
          height: h,
          label: total * segments > 1 ? `${p.name} #${n}` : p.name,
          materialId,
          canRotate,
          sourceItemId: 'preview',
          sourceArea: 'Component',
        });
      }
    }

    const { sheets, skipped } = guillotinePack(
      flat, sheetW, sheetH, materialId, mat.name, kerf, border,
    );

    const sheetArea = (sheetW * sheetH) / 144;
    const consumedSqft = sheets.length * sheetArea;
    const placedSqft = sheets.reduce((s, sh) => s + sheetUsedArea(sh), 0) / 144;
    const efficiency = consumedSqft > 0 ? (placedSqft / consumedSqft) * 100 : 0;
    const impliedWaste = partsSqft > 0 ? (consumedSqft / partsSqft - 1) * 100 : 0;

    out.push({
      material_id: materialId,
      material_name: mat.name,
      unit: mat.unit ?? '',
      sheet_w: sheetW,
      sheet_h: sheetH,
      border,
      kerf,
      grain_locked: grainLocked,
      parts_sqft: partsSqft,
      sqft_with_waste: sqftWithWaste,
      sheet_count: sheets.length,
      consumed_sqft: consumedSqft,
      efficiency_pct: efficiency,
      implied_waste_pct: impliedWaste,
      sheets,
      skipped,
      part_count: flat.length,
    });
  }

  return out.sort((a, b) => b.parts_sqft - a.parts_sqft);
}
